import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Heading, Subheading } from '../catalyst/heading';
import { Text } from '../catalyst/text';
import { Button } from '../catalyst/button';
import { Divider } from '../catalyst/divider';
import { Badge } from '../catalyst/badge';
import {
	DescriptionList,
	DescriptionTerm,
	DescriptionDetails,
} from '../catalyst/description-list';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table';
import { OrderStatusBadge, CodeStatusBadge } from '../components/StatusBadge';
import StatCard from '../components/StatCard';
import { useUser } from '../hooks/useUsers';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { useUserBalance } from '../hooks/useCodes';
import { ArrowLeft, MessageSquare, ShoppingCart, DollarSign, Calendar, Clock, Wallet } from 'lucide-react';
import ResourceLink from '../components/ResourceLink';
import type { Order, OrderSlot, PaymentIntent, ConversationMessage, Code } from '../types/database';

interface OrderWithSlots extends Order {
	order_slots: Pick<OrderSlot, 'slot_name' | 'value' | 'confirmed'>[];
}

function extractSlotDisplayValue(value: Record<string, unknown> | null): string {
	if (value == null) return '';
	if (typeof value === 'string') return value;
	// Handle { name: "..." }, { text: "..." }, or other JSONB shapes
	if (typeof value === 'object') {
		if ('name' in value && typeof value.name === 'string') return value.name;
		if ('text' in value && typeof value.text === 'string') return value.text;
		// Handle address objects with formatted_address
		if ('formatted_address' in value && typeof value.formatted_address === 'string') {
			return value.formatted_address;
		}
		// Handle address objects with parts
		if ('address_line_1' in value || 'city' in value) {
			const parts = [
				value.address_line_1,
				value.address_line_2,
				value.city,
				value.state,
				value.postal_code,
				value.country,
			].filter((p) => typeof p === 'string' && p.length > 0);
			if (parts.length > 0) return parts.join(', ');
		}
		// Fallback: stringify for display
		return JSON.stringify(value);
	}
	return String(value);
}

interface Recipient {
	name: string;
	address: string;
}

function extractUniqueRecipients(orders: OrderWithSlots[]): Recipient[] {
	const seen = new Map<string, Recipient>();

	for (const order of orders) {
		if (!order.order_slots) continue;

		let name = '';
		let address = '';

		for (const slot of order.order_slots) {
			if (slot.slot_name === 'recipient_name' && slot.value != null) {
				name = extractSlotDisplayValue(slot.value);
			}
			if (slot.slot_name === 'recipient_address_validated' && slot.value != null) {
				address = extractSlotDisplayValue(slot.value);
			}
		}

		if (name) {
			const key = `${name}||${address}`;
			if (!seen.has(key)) {
				seen.set(key, { name, address });
			}
		}
	}

	return Array.from(seen.values());
}

function formatCurrency(amount: number, currency: string): string {
	try {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currency.toUpperCase(),
		}).format(amount / 100);
	} catch {
		return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
	}
}

const UserDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const { data: user, loading: userLoading, error: userError } = useUser(id);

	const {
		data: orders,
		loading: ordersLoading,
	} = useSupabaseQuery<OrderWithSlots>(
		(client) =>
			client
				.from('orders')
				.select('*, order_slots(slot_name, value, confirmed)')
				.eq('user_id', id!)
				.order('created_at', { ascending: false }),
		[id]
	);

	const orderIds = orders.map(o => o.id);
	const {
		data: payments,
	} = useSupabaseQuery<PaymentIntent>(
		(client) => {
			let q = client
				.from('payment_intents')
				.select('amount, currency, order_id')
				.eq('status', 'SUCCEEDED');
			if (orderIds.length > 0) {
				q = q.in('order_id', orderIds);
			} else {
				q = q.eq('order_id', 'IMPOSSIBLE_ID_NO_MATCH');
			}
			return q;
		},
		[orderIds.join(',')]
	);

	const {
		data: lastMessages,
	} = useSupabaseQuery<ConversationMessage>(
		(client) =>
			client
				.from('conversation_messages')
				.select('created_at')
				.eq('user_id', id!)
				.order('created_at', { ascending: false })
				.limit(1),
		[id]
	);

	const { data: balanceRows } = useUserBalance(id);

	const { data: ownedCodes } = useSupabaseQuery<Code>(
		(client) => client.from('codes').select('*').eq('owner_id', id!).order('created_at', { ascending: false }),
		[id]
	);

	const balances = useMemo(() => {
		const map = new Map<string, number>();
		for (const row of balanceRows) {
			map.set(row.currency, (map.get(row.currency) || 0) + Number(row.amount));
		}
		return Array.from(map.entries()).map(([currency, amount]) => ({ currency, amount }));
	}, [balanceRows]);

	if (userLoading) {
		return (
			<div className="p-6">
				<Text>Loading user...</Text>
			</div>
		);
	}

	if (userError || !user) {
		return (
			<div className="p-6">
				<div className="flex items-center gap-2">
					<Button plain href="/users">
						<ArrowLeft className="size-4" />
						Back to Users
					</Button>
				</div>
				<div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
					{userError || 'User not found.'}
				</div>
			</div>
		);
	}

	const recipients = extractUniqueRecipients(orders);

	// Compute metrics
	const totalSpent = payments.reduce((sum, p) => sum + p.amount, 0);
	const spentCurrency = payments.length > 0 ? payments[0].currency : 'eur';
	const paidOrderIds = new Set(payments.map((p) => p.order_id));
	const firstOrderDate = orders.length > 0 ? orders[orders.length - 1].created_at : null;
	const lastActiveDate = lastMessages.length > 0 ? lastMessages[0].created_at : null;


	return (
		<div className="p-6">
			{/* Header */}
			<div className="flex items-center gap-2">
				<Button plain href="/users">
					<ArrowLeft className="size-4" />
					Back to Users
				</Button>
			</div>
			<div className="mt-4 flex items-center justify-between">
				<div>
					<Heading level={1}>
						{user.display_name || user.phone_number}
					</Heading>
					<Text className="mt-1">
						Member since {new Date(user.created_at).toLocaleDateString()}
					</Text>
				</div>
				<Button href={`/chats/${user.id}`}>
					<MessageSquare className="size-4" />
					View Chat
				</Button>
			</div>

			<Divider className="my-6" />

			{/* User Info */}
			<Subheading level={2}>User Information</Subheading>
			<DescriptionList className="mt-4">
				<DescriptionTerm>Phone</DescriptionTerm>
				<DescriptionDetails>{user.phone_number}</DescriptionDetails>

				<DescriptionTerm>Email</DescriptionTerm>
				<DescriptionDetails>{user.email || '-'}</DescriptionDetails>

				<DescriptionTerm>Display Name</DescriptionTerm>
				<DescriptionDetails>{user.display_name || '-'}</DescriptionDetails>

				<DescriptionTerm>Stripe Customer ID</DescriptionTerm>
				<DescriptionDetails>
					{user.stripe_customer_id ? (
						<code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-mono dark:bg-zinc-800">
							{user.stripe_customer_id}
						</code>
					) : (
						'-'
					)}
				</DescriptionDetails>

				<DescriptionTerm>Created</DescriptionTerm>
				<DescriptionDetails>
					{new Date(user.created_at).toLocaleString()}
				</DescriptionDetails>

				<DescriptionTerm>Updated</DescriptionTerm>
				<DescriptionDetails>
					{new Date(user.updated_at).toLocaleString()}
				</DescriptionDetails>
			</DescriptionList>

			<Divider className="my-6" />

			{/* User Metrics */}
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
				<StatCard
					title="Total Orders"
					value={orders.length}
					icon={<ShoppingCart className="size-5" />}
				/>
				<StatCard
					title="Total Spent"
					value={formatCurrency(totalSpent, spentCurrency)}
					subtitle={`${payments.length} payment${payments.length !== 1 ? 's' : ''}`}
					icon={<DollarSign className="size-5" />}
				/>
				<StatCard
					title="First Order"
					value={firstOrderDate ? new Date(firstOrderDate).toLocaleDateString() : '—'}
					icon={<Calendar className="size-5" />}
				/>
				<StatCard
					title="Last Active"
					value={lastActiveDate ? new Date(lastActiveDate).toLocaleDateString() : '—'}
					subtitle={lastActiveDate ? new Date(lastActiveDate).toLocaleTimeString() : undefined}
					icon={<Clock className="size-5" />}
				/>
			</div>

			{/* Attribution */}
			{((user as any).first_code_id || (user as any).last_code_id) && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Attribution</Subheading>
						<DescriptionList>
							<DescriptionTerm>First Touch Code</DescriptionTerm>
							<DescriptionDetails>
								{(user as any).first_code_id
									? <ResourceLink id={(user as any).first_code_id} href={`/codes/${(user as any).first_code_id}`} />
									: '—'}
							</DescriptionDetails>
							<DescriptionTerm>Last Touch Code</DescriptionTerm>
							<DescriptionDetails>
								{(user as any).last_code_id
									? <ResourceLink id={(user as any).last_code_id} href={`/codes/${(user as any).last_code_id}`} />
									: '—'}
							</DescriptionDetails>
						</DescriptionList>
					</section>
				</>
			)}

			{/* Credit Balance */}
			{balances.length > 0 && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Credit Balance</Subheading>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
							{balances.map(({ currency, amount }) => (
								<StatCard
									key={currency}
									title={`${currency.charAt(0).toUpperCase() + currency.slice(1)} Balance`}
									value={currency === 'product' ? amount : `€${(amount / 100).toFixed(2)}`}
									icon={<Wallet className="h-5 w-5" />}
								/>
							))}
						</div>
					</section>
				</>
			)}

			{/* Owned Codes */}
			{ownedCodes.length > 0 && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Owned Codes ({ownedCodes.length})</Subheading>
						<Table dense>
							<TableHead>
							<TableRow>
								<TableHeader>Code</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{ownedCodes.map((code) => (
								<TableRow key={code.id} href={`/codes/${code.id}`}>
									<TableCell className="font-mono font-medium">{code.code}</TableCell>
									<TableCell><CodeStatusBadge status={code.status} /></TableCell>
									<TableCell className="text-zinc-500">{new Date(code.created_at).toLocaleDateString()}</TableCell>
								</TableRow>
								))}
							</TableBody>
						</Table>
					</section>
				</>
			)}

			<Divider className="my-6" />

			{/* Orders History */}
			<Subheading level={2}>Orders History</Subheading>
			{ordersLoading ? (
				<Text className="mt-4">Loading orders...</Text>
			) : orders.length === 0 ? (
				<Text className="mt-4">No orders yet.</Text>
			) : (
				<Table className="mt-4">
					<TableHead>
						<TableRow>
							<TableHeader>Order ID</TableHeader>
							<TableHeader>Status</TableHeader>
							<TableHeader>Recipient</TableHeader>
							<TableHeader>Message</TableHeader>
							<TableHeader>Payment</TableHeader>
							<TableHeader>Created</TableHeader>
						</TableRow>
					</TableHead>
					<TableBody>
						{orders.map((order) => {
							const recipientSlot = order.order_slots?.find((s) => s.slot_name === 'recipient_name');
							const messageSlot = order.order_slots?.find((s) => s.slot_name === 'message_text');
							const recipientName = recipientSlot ? extractSlotDisplayValue(recipientSlot.value) : '';
							const messageText = messageSlot ? extractSlotDisplayValue(messageSlot.value) : '';
							const isPaid = paidOrderIds.has(order.id);

							return (
								<TableRow key={order.id} href={`/orders/${order.id}`}>
									<TableCell>
										<ResourceLink id={order.id} />
									</TableCell>
									<TableCell>
										<OrderStatusBadge status={order.status} />
									</TableCell>
									<TableCell>
										{recipientName || <span className="text-zinc-400">—</span>}
									</TableCell>
									<TableCell className="max-w-[200px] truncate">
										{messageText ? (
											<span title={messageText}>
												{messageText.length > 40 ? messageText.slice(0, 40) + '…' : messageText}
											</span>
										) : (
											<span className="text-zinc-400">—</span>
										)}
									</TableCell>
									<TableCell>
										<Badge color={isPaid ? 'green' : 'zinc'}>
											{isPaid ? 'Paid' : 'Unpaid'}
										</Badge>
									</TableCell>
									<TableCell>
										{new Date(order.created_at).toLocaleDateString()}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			)}

			<Divider className="my-6" />

			{/* Unique Recipients */}
			<Subheading level={2}>Unique Recipients</Subheading>
			{ordersLoading ? (
				<Text className="mt-4">Loading...</Text>
			) : recipients.length === 0 ? (
				<Text className="mt-4">No recipients found.</Text>
			) : (
				<div className="mt-4 space-y-3">
					{recipients.map((r, i) => (
						<div
							key={i}
							className="rounded-lg border border-zinc-950/5 bg-white p-4 dark:border-white/10 dark:bg-zinc-900"
						>
							<p className="font-medium text-zinc-900 dark:text-white">{r.name}</p>
							{r.address && (
								<p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
									{r.address}
								</p>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default UserDetailPage;
